import { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import { useStatusLabel } from 'views/Refund/components/StatusHistoryModal';

/** 退货单状态展示：根据平台与 displayStatus 推导出最终展示文案 */
export function useReturnStatusDisplay(
  platform: Conversation.Trade.TradeWithLogistics['platform'],
  displayStatus: Conversation.Trade.RefundReason['displayStatus'],
) {
  const { t } = useTranslate();
  const lazadaStatusLabel = useStatusLabel();

  const statusLabels = useMemo(
    () =>
      ({
        IN_REVIEW: t('domain.returnRefund.status.underReview'),
        WAITING_EVIDENCE: t('domain.returnRefund.status.waitingEvidence'),
        RETURN_PROCESSING: t('domain.returnRefund.status.returnProcessing'),
        PENDING_RESPOND: t('domain.returnRefund.status.pendingResponse'),
        REFUND_APPROVED: t('domain.returnRefund.status.refundApproved'),
        WAITING_BUYER_RESPONSE: t('domain.returnRefund.status.waitingBuyerResponse'),
        RETURN_IN_PROGRESS: t('domain.returnRefund.status.returnInProgress'),
        REFUND_COMPLETED: t('domain.returnRefund.status.refundCompleted'),
        DISPUTE_RESOLVED: t('domain.returnRefund.status.disputeResolved'),
        CANCELLED: t('domain.returnRefund.status.cancelled'),
        UNKNOWN: t('domain.returnRefund.status.unknown', {
          platform: platformLabelMap[toLowerCase(platform)],
        }),
        RETURN_REFUND_PENDING: t('domain.returnRefund.status.returnRefundPending'),
        REFUND_REQUESTED: t('domain.returnRefund.status.refundRequested'),
        VALIDATION_IN_PROGRESS: t('domain.returnRefund.status.validationInProgress'),
        VALIDATION_IN_PROGRESS_V2: t('domain.returnRefund.status.validationInProgress'),
        EVIDENCE_SUBMITTED: t('domain.returnRefund.status.evidenceSubmitted'),
        COMPENSATION_APPROVED: t('domain.returnRefund.status.compensationApproved'),
        DISPUTE_REJECT: t('domain.returnRefund.status.disputeRejected'),
        DISPUTE_WAIT_EVIDENCE: t('domain.returnRefund.status.waitingEvidenceAfterDispute'),
        REFUND_APPROVED_2: t('domain.returnRefund.status.refundApproved'),
        ...lazadaStatusLabel,
      } as const),
    [t, lazadaStatusLabel, platform],
  );

  const displayStatusText = useMemo(() => {
    if (platform === 'mercado') {
      return t(`${toLowerCase(platform)}.returnStatus.${displayStatus}`);
    }
    return statusLabels[displayStatus as keyof typeof statusLabels] ?? displayStatus ?? '';
  }, [platform, displayStatus, t, statusLabels]);

  return {
    /** 有值时可展示，空字符串表示不展示 */
    displayStatusText: displayStatusText || null,
  };
}
